import {Modal, TextInput} from '@/components/atoms'
import {SaveCancelFooter, SectionTitle} from '@/components/molecules'
import {PageContainer} from '@/components/organisms'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {ROUTES} from '@/utils/constants/routesConstants'
import {toSafeRouteParam} from '@/utils/security/pathSafety'
import {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'

const Section = ({id, title}) => {
  return <SectionTitle id={id} label={title} />
}

export const ProductDisplayEdit = ({formData, setFormData, handleSave}) => {
  const [isSaving, setIsSaving] = useState(false)
  const [isCancel, setIsCancel] = useState(false)
  const languages = useFeatureFlagLanguages()
  const countryCode = useSelector(selectCountryCode)
  const isIndia = countryCode === 'IND'

  // A single state variable for all local form data
  const [localDisplay, setLocalDisplay] = useState({
    displayName: formData?.display?.displayName || {},
    displaySize: formData?.display?.displaySize || {},
    flavor: formData?.display?.flavor || {},
    shortDescription: formData?.display?.shortDescription || {},
    longDescription: formData?.display?.longDescription || {},
    keywords: formData?.display?.keywords || {},
    displaySizeId: formData?.display?.displaySizeId || '',
  })

  // Use a ref to track if we're in the middle of a save operation
  const isSavingRef = useRef(false)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {id} = useParams()

  // Sync local state when formData changes from parent
  useEffect(() => {
    // Only update if we're not in the middle of a save operation
    if (formData?.display && !isSavingRef.current) {
      setLocalDisplay({
        displayName: formData.display.displayName || {},
        displaySize: formData.display.displaySize || {},
        flavor: formData.display.flavor || {},
        shortDescription: formData.display.shortDescription || {},
        longDescription: formData.display.longDescription || {},
        keywords: formData.display.keywords || {},
        displaySizeId: formData.display.displaySizeId || '',
      })
    }
  }, [formData])

  const handleInputChange = (lang, field, value) => {
    // Update local state for language-specific fields
    const updatedLocalDisplay = {
      ...localDisplay,
      [field]: {
        ...localDisplay[field],
        [lang]: value,
      },
    }

    // Update local state
    setLocalDisplay(updatedLocalDisplay)

    // Update parent state immediately to ensure persistence across tabs
    setFormData({
      ...formData,
      display: {
        ...formData.display,
        ...updatedLocalDisplay,
      },
    })
  }

  const handleFieldChange = (field, value) => {
    const updatedLocalDisplay = {...localDisplay, [field]: value}
    setLocalDisplay(updatedLocalDisplay)
    setFormData({
      ...formData,
      display: {...formData.display, ...updatedLocalDisplay},
    })
  }

  const saveChanges = () => {
    // Update the parent formData with our local changes
    setFormData({
      ...formData,
      display: {
        ...formData.display,
        ...localDisplay,
      },
    })
  }

  const handleSaveAction = async () => {
    try {
      setIsSaving(true)
      // Set the ref to true during save operation
      isSavingRef.current = true

      // First update the parent state
      saveChanges()

      // Then pass the updated formData to the save handler
      const updatedFormData = {
        ...formData,
        display: {
          ...formData.display,
          ...localDisplay,
        },
      }

      await handleSave(updatedFormData)
    } catch (error) {
      dispatch(
        setToastNotifications({
          show: true,
          message: `Error saving Display Information: ${error}`,
          type: 'error',
        })
      )
    } finally {
      setIsSaving(false)
      // Reset the ref after save operation
      isSavingRef.current = false
    }
  }

  const handleCancel = () => {
    setIsCancel(!isCancel)
  }

  const onClickPrimaryButton = () => {
    const safeId = toSafeRouteParam(id)
    navigate(safeId ? ROUTES.PRODUCT_REVIEW.replace(':id', safeId) : ROUTES.PRODUCTS_LIST, {
      state: safeId ? {selectedTabNumb: 2} : undefined,
    })
  }

  const onClickSecondaryButton = () => {
    setIsCancel(false)
  }

  const formError = product => {
    const mandatoryDisplayNameCountries = ['MX', 'GT', 'NA', 'CO']

    if (!product?.display) return false

    // Validate displayName - mandatory for MX, GT, NA, CO
    if (mandatoryDisplayNameCountries.includes(countryCode)) {
      const displayName = product.display?.displayName
      if (!displayName || typeof displayName !== 'object') {
        return false
      }
      // Check that all language fields have non-empty values
      const hasEmptyValues = languages.some(lang => !displayName[lang.code] || !displayName[lang.code].trim())
      if (hasEmptyValues) {
        return false
      }

      // Validate longDescription - mandatory for MX, GT, NA, CO
      const longDescription = product.display?.longDescription
      if (!longDescription || typeof longDescription !== 'object') {
        return false
      }
      const hasEmptyLongDesc = languages.some(lang => !longDescription[lang.code] || !longDescription[lang.code].trim())
      if (hasEmptyLongDesc) {
        return false
      }
    }

    // Validate displayName for India - only 'en' language is mandatory
    if (isIndia) {
      const displayName = product.display?.displayName
      if (!displayName || typeof displayName !== 'object' || !displayName['en'] || !displayName['en'].trim()) {
        return false
      }
      // Validate longDescription for India
      const longDescription = product.display?.longDescription
      if (!longDescription || typeof longDescription !== 'object') {
        return false
      }
      const hasEmptyValues = Object.values(longDescription).some(value => !value || !value.trim())
      if (hasEmptyValues || Object.keys(longDescription).length === 0) {
        return false
      }
    }

    return true
  }
  return (
    <PageContainer>
      <div className="productedit-display">
        <Section id={'Display Information'} title="Display Information" />
        <div className="sc-border grid grid-cols-3 gap-2 pb-1"></div>
        <TextInput
          id={'display-size-id'}
          className="mar-top-txt text-sm"
          isDark={true}
          label="Display Size ID"
          placeholderText="Add Display Size ID"
          value={localDisplay.displaySizeId || ''}
          onChange={value => {
            const intValue = value.replace(/\D/g, '')
            handleFieldChange('displaySizeId', intValue)
          }}
          disabled={isSaving}
        />
        <div className="grid grid-cols-3 gap-6">
          {languages.map(lang => (
            <div key={lang.code} className="p-4">
              <h3 className="mb-2 text-lg font-semibold">{lang?.language}</h3>
              <TextInput
                id={`display-name-${lang.code}`}
                className=" mar-top-txt text-sm"
                isDark={true}
                label="Display Name"
                placeholderText="Add Display Name"
                value={localDisplay.displayName?.[lang.code] || ''}
                onChange={value => handleInputChange(lang.code, 'displayName', value)}
                disabled={isSaving}
                required={isIndia ? lang?.code === 'en' : true}
              />
              <TextInput
                id={`display-size-${lang.code}`}
                className="  mar-top-txt text-sm"
                isDark={true}
                label="Display Size"
                placeholderText="Add Display Size"
                value={localDisplay.displaySize?.[lang.code] || ''}
                onChange={value => handleInputChange(lang.code, 'displaySize', value)}
                disabled={isSaving}
              />
              <TextInput
                id={`flavor-${lang.code}`}
                className="mar-top-txt  text-sm "
                isDark={true}
                label="Product Flavor"
                placeholderText="Add Product Flavor"
                value={localDisplay.flavor?.[lang.code] || ''}
                onChange={value => handleInputChange(lang.code, 'flavor', value)}
                disabled={true}
              />
              <TextInput
                id={`short-description-${lang.code}`}
                className="mar-top-txt  text-sm"
                isDark={true}
                label="Short Description"
                placeholderText="Add Short Description"
                value={localDisplay.shortDescription?.[lang.code] || ''}
                onChange={value => handleInputChange(lang.code, 'shortDescription', value)}
                disabled={true} // AC-3: BU user should not be allowed to edit Short Description
              />
              <TextInput
                id={`keywords-${lang.code}`}
                className="mar-top-txt  text-sm"
                isDark={true}
                label="Keywords"
                placeholderText="Add Keywords"
                value={localDisplay.keywords?.[lang.code] || ''}
                onChange={value => handleInputChange(lang.code, 'keywords', value)}
                disabled={isSaving}
              />
              <TextInput
                id={`long-description-${lang.code}`}
                className="mar-top-txt  text-sm"
                isDark={true}
                label="Long Description"
                placeholderText="Add Long Description"
                value={localDisplay.longDescription?.[lang.code] || ''}
                onChange={value => handleInputChange(lang.code, 'longDescription', value)}
                disabled={isSaving}
                required={true}
              />
            </div>
          ))}
        </div>

        {isCancel ? (
          <Modal
            type={MODAL_TYPE.SMALL}
            showModal={isCancel}
            onCloseModal={handleCancel}
            title="Are you sure you want to cancel?"
            text={'The changes will not be saved.'}
            showPrimaryButton={true}
            primaryButtonText="Yes"
            onClickPrimaryButton={onClickPrimaryButton}
            showSecondaryButton={true}
            secondaryButtonText="No"
            onClickSecondaryButton={onClickSecondaryButton}
            footerProps={{
              size: 'medium',
              type: 'primary',
            }}
            isDark={true}
          />
        ) : null}

        <SaveCancelFooter
          handleCancel={handleCancel}
          nextAction={handleSaveAction}
          isReview={true}
          hideSaveDraft={true}
          isLoading={isSaving}
          FormError={!formError(formData)}
        />
      </div>
    </PageContainer>
  )
}

export default ProductDisplayEdit
